import React, { useState } from 'react';
import { Plus, Upload, Eye, DollarSign, Calendar, Clock, CheckCircle, XCircle, AlertCircle, Filter, Search, Info } from 'lucide-react';

const Reimbursements = () => {
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    month: ''
  });

  const [newClaim, setNewClaim] = useState({
    category: '',
    amount: '',
    date: '',
    description: '',
    receipt: null
  });

  // Mock data since no APIs exist for reimbursements
  const reimbursements = [
    {
      id: 1,
      category: 'Travel',
      amount: 2500,
      submitted_date: '2024-01-15',
      expense_date: '2024-01-10',
      description: 'Client meeting travel expenses',
      status: 'approved',
      approved_date: '2024-01-18',
      paid_date: '2024-01-20',
      receipt_uploaded: true
    },
    {
      id: 2,
      category: 'Medical',
      amount: 1200,
      submitted_date: '2024-01-20',
      expense_date: '2024-01-18',
      description: 'Medical checkup and medicines',
      status: 'pending',
      approved_date: null,
      paid_date: null,
      receipt_uploaded: true
    },
    {
      id: 3,
      category: 'Internet',
      amount: 800,
      submitted_date: '2024-01-25',
      expense_date: '2024-01-01',
      description: 'Monthly internet bill for WFH',
      status: 'rejected',
      approved_date: null,
      paid_date: null,
      receipt_uploaded: false,
      rejection_reason: 'Receipt not clear, please resubmit with better quality'
    }
  ];

  const categories = ['Travel', 'Medical', 'Internet', 'Phone', 'Food', 'Office Supplies', 'Training', 'Other'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const summary = {
    total_submitted: reimbursements.reduce((sum, claim) => sum + claim.amount, 0),
    approved_amount: reimbursements.filter(r => r.status === 'approved').reduce((sum, claim) => sum + claim.amount, 0),
    pending_amount: reimbursements.filter(r => r.status === 'pending').reduce((sum, claim) => sum + claim.amount, 0),
    rejected_amount: reimbursements.filter(r => r.status === 'rejected').reduce((sum, claim) => sum + claim.amount, 0)
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'approved':
        return { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Approved' };
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending' };
      case 'rejected':
        return { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' };
      case 'paid':
        return { color: 'bg-blue-100 text-blue-800', icon: DollarSign, text: 'Paid' };
      default:
        return { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, text: 'Unknown' };
    }
  };

  const handleSubmitClaim = (e) => {
    e.preventDefault();
    // Mock submission - in real app, this would call an API
    alert('Reimbursement claim submitted successfully! (API not implemented)');
    setShowSubmitModal(false);
    setNewClaim({
      category: '',
      amount: '',
      date: '',
      description: '',
      receipt: null
    });
  };

  const viewClaimDetails = (claim) => {
    setSelectedClaim(claim);
    setShowDetailsModal(true);
  };

  const filteredReimbursements = reimbursements.filter(claim => {
    if (filters.status && claim.status !== filters.status) return false;
    if (filters.category && claim.category !== filters.category) return false;
    if (filters.month) {
      const claimMonth = new Date(claim.submitted_date).getMonth();
      if (claimMonth !== parseInt(filters.month) - 1) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reimbursements</h1>
            <p className="text-gray-600 mt-2">Submit and track your expense reimbursement claims</p>
          </div>
          <button
            onClick={() => setShowSubmitModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Submit Claim
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-blue-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Submitted</p>
                <p className="text-xl font-bold text-gray-900">₹{summary.total_submitted.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-xl font-bold text-gray-900">₹{summary.approved_amount.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-xl font-bold text-gray-900">₹{summary.pending_amount.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <XCircle className="h-8 w-8 text-red-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Rejected</p>
                <p className="text-xl font-bold text-gray-900">₹{summary.rejected_amount.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.month}
                onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
              >
                <option value="">All Months</option>
                {months.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ status: '', category: '', month: '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center"
              >
                <Filter className="h-4 w-4 mr-2" />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Reimbursements Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReimbursements.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No reimbursements found</td>
                  </tr>
                ) : (
                  filteredReimbursements.map((claim) => {
                    const statusInfo = getStatusInfo(claim.status);
                    const StatusIcon = statusInfo.icon;
                    
                    return (
                      <tr key={claim.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(claim.expense_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {claim.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-xs truncate">{claim.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₹{claim.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <StatusIcon className={`h-4 w-4 mr-2 ${claim.status === 'approved' ? 'text-green-500' : claim.status === 'pending' ? 'text-yellow-500' : 'text-red-500'}`} />
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                              {statusInfo.text}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => viewClaimDetails(claim)}
                            className="text-blue-600 hover:text-blue-900 flex items-center"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Submit Claim Modal */}
        {showSubmitModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Submit Reimbursement Claim</h3>
                <form onSubmit={handleSubmitClaim} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={newClaim.category}
                        onChange={(e) => setNewClaim(prev => ({ ...prev, category: e.target.value }))}
                      >
                        <option value="">Select Category</option>
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={newClaim.amount}
                        onChange={(e) => setNewClaim(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expense Date</label>
                    <input
                      type="date"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newClaim.date}
                      onChange={(e) => setNewClaim(prev => ({ ...prev, date: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      rows="3"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newClaim.description}
                      onChange={(e) => setNewClaim(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe your expense..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Receipt</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to upload receipt or drag and drop</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG, PDF up to 10MB</p>
                      <input
                        type="file"
                        className="hidden"
                        accept=".png,.jpg,.jpeg,.pdf"
                        onChange={(e) => setNewClaim(prev => ({ ...prev, receipt: e.target.files[0] }))}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowSubmitModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Submit Claim
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Claim Details Modal */}
        {showDetailsModal && selectedClaim && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Claim Details</h3>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Claim Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Category:</span>
                        <span className="text-sm font-medium text-gray-900">{selectedClaim.category}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Amount:</span>
                        <span className="text-sm font-medium text-gray-900">₹{selectedClaim.amount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Expense Date:</span>
                        <span className="text-sm font-medium text-gray-900">{new Date(selectedClaim.expense_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Submitted Date:</span>
                        <span className="text-sm font-medium text-gray-900">{new Date(selectedClaim.submitted_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Status:</span>
                        <span className={`text-sm font-medium ${selectedClaim.status === 'approved' ? 'text-green-600' : selectedClaim.status === 'pending' ? 'text-yellow-600' : 'text-red-600'}`}>
                          {selectedClaim.status.charAt(0).toUpperCase() + selectedClaim.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Processing Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      {selectedClaim.approved_date && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Approved Date:</span>
                          <span className="text-sm font-medium text-gray-900">{new Date(selectedClaim.approved_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      {selectedClaim.paid_date && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Paid Date:</span>
                          <span className="text-sm font-medium text-gray-900">{new Date(selectedClaim.paid_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Receipt:</span>
                        <span className={`text-sm font-medium ${selectedClaim.receipt_uploaded ? 'text-green-600' : 'text-red-600'}`}>
                          {selectedClaim.receipt_uploaded ? 'Uploaded' : 'Not Uploaded'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Description</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700">{selectedClaim.description}</p>
                  </div>
                </div>

                {selectedClaim.rejection_reason && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-red-900 mb-3">Rejection Reason</h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-700">{selectedClaim.rejection_reason}</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-6">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reimbursement Policy */}
        {/* <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">📋 Reimbursement Policy</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <p className="font-medium mb-2">Submission Guidelines:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Submit claims within 30 days of expense</li>
                <li>Original receipts must be uploaded</li>
                <li>Provide detailed description of expense</li>
                <li>Maximum claim amount: ₹50,000 per month</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-2">Processing Timeline:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Initial review: 2-3 business days</li>
                <li>Approval process: 5-7 business days</li>
                <li>Payment processing: 7-10 business days</li>
                <li>Direct deposit to salary account</li>
              </ul>
            </div>
          </div>
        </div> */}

        {/* No API Available Notice */}
        {/* <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <Info className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">API Integration Required</h3>
              <p className="text-red-800 mt-2">
                This page displays mock data. The following APIs need to be implemented:
              </p>
              <ul className="list-disc list-inside text-red-700 mt-2 text-sm">
                <li><code>GET /api/reimbursements</code> - Fetch reimbursement claims</li>
                <li><code>POST /api/reimbursements</code> - Submit new claims</li>
                <li><code>GET /api/reimbursements/{id}</code> - Get claim details</li>
                <li><code>POST /api/reimbursements/{id}/upload</code> - Upload receipts</li>
              </ul>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Reimbursements;